import React from "react";

// Links from the Bio component point at the ids below (e.g. #pub-lie-detector).
// If you rename an id here, update Bio.js too.

const Pub = ({ id, title, venue, links, children }) => (
  <div className="row" id={id}>
    <div className="twelve columns pubEntry" style={{ marginLeft: 0 }}>
      <div className="eight columns subheadSmaller">
        <h6>{title}</h6>
        <p className="venue">{venue}</p>
        {links && links.length > 0 && (
          <p className="metaLinks">
            {links.map(([label, url], i) => (
              <React.Fragment key={url}>
                {i > 0 && " · "}
                <a href={url}>{label}</a>
              </React.Fragment>
            ))}
          </p>
        )}
        {children && (
          <details>
            <summary>Abstract</summary>
            <p className="abstract">{children}</p>
          </details>
        )}
      </div>
    </div>
  </div>
);

const Ben = <a href="https://www.levinstein.org/">Ben Levinstein</a>;
const Aydin = <a href="https://aydinmohseni.com/">Aydin Mohseni</a>;
const Jack = <a href="https://jackv.co">Jacob VanDrunen</a>;
const Bruce = <a href="https://brucemrushing.com/">Bruce Rushing</a>;

const Research = () => {
  return (
    <div>
      <style>{`
        .pubList .pubEntry {
          margin-bottom: 2.75rem;
        }
        .pubList [id^="pub-"] {
          scroll-margin-top: 2rem;
        }
        .pubList h6 {
          margin-bottom: 0.35rem;
        }
        .pubList .venue {
          font-weight: 600;
          margin-bottom: 0.15rem;
        }
        .pubList .metaLinks {
          font-size: 0.9em;
          margin-bottom: 0.15rem;
        }
        .pubList details {
          margin-top: 0.45rem;
        }
        .pubList summary {
          list-style: none;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          font-size: 0.75em;
          letter-spacing: 0.09em;
          text-transform: uppercase;
          color: #a00000;
          opacity: 0.8;
          -webkit-user-select: none;
          user-select: none;
        }
        .pubList summary::-webkit-details-marker {
          display: none;
        }
        .pubList summary::before {
          content: "";
          border-left: 0.35rem solid currentColor;
          border-top: 0.26rem solid transparent;
          border-bottom: 0.26rem solid transparent;
          transition: transform 0.15s ease;
        }
        .pubList details[open] summary::before {
          transform: rotate(90deg);
        }
        .pubList summary:hover {
          opacity: 1;
        }
        .pubList details .abstract {
          margin-top: 0.8rem;
          margin-bottom: 0;
          padding-left: 0.9rem;
          border-left: 1px solid rgba(0, 0, 0, 0.1);
        }
        .sectionNav {
          font-size: 0.8em;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          margin-bottom: 3rem;
        }
        .sectionNav a {
          margin-right: 1.25rem;
          text-decoration: none;
        }
      `}</style>

      <div className="row">
        <div
          className="twelve columns pubList"
          style={{ marginLeft: 0, marginBottom: "5rem" }}
        >
          <div
            style={{
              marginTop: "0.75rem",
              marginBottom: "2.5rem",
              maxWidth: "38rem",
            }}
          >
            <p
              style={{
                fontWeight: 500,
                fontSize: "0.95em",
                lineHeight: "1.45",
                marginBottom: "0.5rem",
                color: "#a00000",
              }}
            >
              <a
                href="https://www.poetryfoundation.org/poems/45134/ode-to-the-west-wind"
                style={{
                  fontWeight: "inherit",
                  textDecoration: "none",
                  color: "inherit",
                }}
              >
                Drive my dead thoughts over the universe<br />
                Like wither&apos;d leaves to quicken a new birth!
              </a>
            </p>
            <p
              style={{
                fontSize: "0.85em",
                margin: 0,
                opacity: 0.7,
              }}
            >
              — Percy Bysshe Shelley, <em>Ode to the West Wind</em>
            </p>
            <hr
              style={{
                marginTop: "1.75rem",
                border: "none",
                borderTop: "1px solid rgba(0,0,0,0.08)",
                width: "6rem",
              }}
            />
          </div>

          <p className="sectionNav">
            <a href="#book">Book</a>
            <a href="#articles">Articles</a>
            <a href="#reviews">Book Reviews</a>
            <a href="#inprogress">Under Review &amp; In Progress</a>
          </p>

          {/* ---------------- BOOK ---------------- */}

          <h2 id="book">Book</h2>

          <Pub
            id="pub-book"
            title="Radical AI Interpretability"
            venue={<>Cambridge University Press (forthcoming), with {Ben}</>}
            links={[
              ["Draft", "https://philpapers.org/archive/HERRAI-5.pdf"],
              ["arXiv", "https://arxiv.org/abs/2606.26523"],
            ]}
          >
            We develop a framework for interpreting AI systems as agents, drawing on the philosophical tradition of radical interpretation and the
            tools of mechanistic interpretability. The core question is: given the computational facts about a system, how do we solve for its beliefs,
            desires, and meanings? This matters increasingly for safety. We want to be able to trust the systems we deploy, whether by understanding
            their goals or, more modestly, by reliably detecting deception. Interpretability researchers are building tools to read beliefs and desires
            off a model&apos;s internals, but there is no settled account of when such a tool has succeeded. This book supplies one. We propose criteria
            on both representationalist and interpretationist approaches, and tie each to tests current interpretability methods can carry out. A central
            lesson is that these attributions cannot be made piecemeal. Beliefs, desires, and the propositional structure they presuppose are jointly
            constrained, and a method that fixes one while measuring the others inherits whatever distortions that introduces. This holism becomes
            pressing for AI systems, which may not share the interpreter&apos;s concepts. However, it also provides leverage: a system&apos;s attitudes
            constrain its propositional structure, that structure constrains which attitudes can be attributed, and mechanistic interpretability can
            help us measure both.
          </Pub>

          {/* ---------------- ARTICLES ---------------- */}

          <h2 id="articles" style={{ marginTop: "4rem" }}>Articles</h2>

          <Pub
            id="pub-social-sciences"
            title="Why Are the Social Sciences Hard? Two New Hypotheses"
            venue={
              <>
                <a href="https://www.sciencedirect.com/science/article/pii/S003936812600083X">Studies in History and Philosophy of Science</a> (2026),
                with {Aydin} and{" "}
                <a href="https://uni-tuebingen.de/en/faculties/faculty-of-economics-and-social-sciences/subjects/department-of-social-sciences/education-sciences-and-psychology/institute/staff/orona-gabe-dr/">Gabe Avakian Orona</a>
              </>
            }
            links={[
              ["Blog version", "https://www.lesswrong.com/posts/3dkKgHEnkjAXuc3jQ/why-are-the-human-sciences-hard-two-new-hypotheses"],
            ]}
          >
            Debates about whether the social sciences are <em>harder</em> than the natural sciences rarely analyze what discipline-level hardness
            amounts to. We offer a simple formal framework that models disciplines as sets of prediction tasks with associated difficulty, and links
            judgments of hardness to a discipline&apos;s best-achieved performance on such tasks and to the psychological <em>impressiveness</em> of
            those successes. Within this framework we develop two hypotheses about why the social sciences appear hard. The <em>rigid demands</em>{" "}
            hypothesis holds that, in the social sciences, we are precommitted to a narrow class of prediction tasks, which restricts the usual
            strategy of changing the question to a more tractable one and thereby lowers expected best performance. The <em>fruit in the hand</em>{" "}
            hypothesis holds that, due to evolutionary and cultural pressures, humans already solve many low-difficulty prediction tasks about human
            behaviour, so these do not register as scientific successes and harder residual tasks dominate our impressions. We show that, even if
            task-level difficulty distributions were identical across domains, these mechanisms would make the social sciences appear harder than the
            natural sciences. This undermines straightforward inferences from differences in headline successes to claims about the intrinsic
            difficulty of the social domain. This framework opens new lines of inquiry regarding perceptions of the difficulty of the sciences.
          </Pub>

          <Pub
            id="pub-natural-salience"
            title="Naturalizing Natural Salience"
            venue={
              <>
                <a href="https://www.journals.uchicago.edu/doi/10.1086/725654">British Journal for the Philosophy of Science</a> (2026), with {Jack}
              </>
            }
          >
            Grice, Lewis, and Skyrms proposed similar distinctions between kinds of meaning.
            The meaning of terms in human language, as Lewis and Skyrms had it, is &ldquo;conventional.&rdquo;
            Skyrms presented models showing how it is possible for conventional meaning to evolve in a population
            without reliance on pre-existing meaning. But one might think of conventionality as coming in degrees,
            based on whether the evolutionary process begins with &ldquo;natural saliences.&rdquo; We propose a theory of
            natural salience and several extensions of Skyrms&apos;s models to capture this notion. These models reveal
            that natural saliences can hinder, as well as help, the evolution of language.
          </Pub>

          <Pub
            id="pub-misalignment"
            title="A Decision-Theoretic Approach for Managing Misalignment"
            venue={
              <>
                <a href="https://ojs.aaai.org/index.php/IASEAI/article/view/43026">Proceedings of the Second International Association for Safe &amp; Ethical AI Conference</a> (2026),
                with <a href="https://www.linkedin.com/in/abinav-chari1">Abinav Chari</a>,{" "}
                <a href="https://www.linkedin.com/in/isabelle-athena-qian">Isabelle Qian</a>,{" "}
                <a href="https://www.sreesharvesh.com/">Sree Sharvesh</a>, and {Ben}
              </>
            }
            links={[["arXiv", "https://arxiv.org/abs/2512.15584"]]}
          >
            When should we delegate decisions to AI systems? While the value alignment literature has developed techniques for shaping AI values,
            less attention has been paid to how to determine, under uncertainty, when imperfect alignment is good enough to justify delegation.
            We argue that rational delegation requires balancing an agent&apos;s value (mis)alignment with its epistemic accuracy and its reach (the
            acts it has available). This paper introduces a formal, decision-theoretic framework to analyze this tradeoff precisely accounting
            for a principal&apos;s uncertainty about these factors. Our analysis reveals a sharp distinction between two delegation scenarios. First,
            universal delegation (trusting an agent with any problem) demands near-perfect value alignment and total epistemic trust, conditions
            rarely met in practice. Second, we show that context-specific delegation can be optimal even with significant misalignment. An agent&apos;s
            superior accuracy or expanded reach may grant access to better overall decision problems, making delegation rational in expectation.
            We develop a novel scoring framework to quantify this ex ante decision. Ultimately, our work provides a principled method for determining
            when an AI is aligned enough for a given context, shifting the focus from achieving perfect alignment to managing the risks and rewards
            of delegation under uncertainty.
          </Pub>

          <Pub
            id="pub-deference"
            title="Deference and Decision"
            venue={
              <>
                <a href="https://link.springer.com/article/10.1007/s11238-025-10036-0">Theory and Decision</a> (2025),
                with <a href="https://gerardrothfus.com/">Gerard Rothfus</a>
              </>
            }
            links={[["Free link", "https://rdcu.be/ekuAY"]]}
          >
            Consider two principles of rational decision making. One says that you should never use the value of events that are inconsistent with your evidence to assess
            what to do. The other says that, if you know what a more informed version of yourself would do, then you should already do likewise with your current information. We show
            that no decision theory which agrees with evidential decision theory (EDT) and causal decision theory (CDT) whenever they agree can satisfy both principles. In particular,
            assuming that agents update via Bayesian conditioning, EDT satisfies the former and violates the latter, while CDT violates the former and satisfies the latter.
            We show that the causalist can satisfy the first (at the cost of then violating the second) if she either uses a learning rule that changes her beliefs about
            counterfactuals in a particular way or uses a decision rule more in line with a rule proposed by Hitchcock (2016). We then employ these results to illuminate
            a core tension at the heart of the debate between evidentialists and causalists.
          </Pub>

          <Pub
            id="pub-lie-detector"
            title="Still No Lie Detector for Language Models: Probing Empirical and Conceptual Roadblocks"
            venue={
              <>
                <a href="https://doi.org/10.1007/s11098-023-02094-3">Philosophical Studies</a> (2025), with {Ben}
              </>
            }
            links={[
              ["Free link", "https://link.springer.com/epdf/10.1007/s11098-023-02094-3?sharing_token=SQMI6yIG-Y3_64NhDmPODPe4RwlQNchNByi7wbcMAY4V8cw3OTXTDNDj1o4J90P-1orE5NH7T79Dpp9kZfaa9-j_1GbmsAUVUqokbkR7Q9PTKBra4xq8wm8FZKqZl3dix_ZotmX5xKFy1OlLO3EqktXy-y4pCHPcdIh2Z6wU9Yk%3D"],
            ]}
          >
            We consider the questions of whether or not large language models (LLMs) have beliefs, and, if they do, how we might measure them.
            First, we consider whether or not we should expect LLMs to have something like beliefs in the first place. We consider some
            recent arguments aiming to show that LLMs cannot have beliefs. We show that these arguments are misguided. We provide a more productive
            framing of questions surrounding the status of beliefs in LLMs, and highlight the empirical nature of the problem. With this lesson in hand,
            we evaluate two existing approaches for measuring the beliefs of LLMs, one due to Azaria and Mitchell (2023) and the other to
            Burns et al. (2022). Moving from the armchair to the desk chair, we provide empirical results that show that these methods fail
            to generalize in very basic ways. We then argue that, even if LLMs have beliefs, these methods are unlikely to be successful for conceptual
            reasons. Thus, there is still no lie-detector for LLMs. We conclude by suggesting some concrete paths for future work.
          </Pub>

          <Pub
            id="pub-sifting"
            title="Sifting the Signal from the Noise"
            venue={
              <>
                <a href="https://doi.org/10.1086/720805">British Journal for the Philosophy of Science</a> (2025), with {Jack}
              </>
            }
          >
            Signaling games are useful for understanding how language emerges. In the standard models the dynamics in some sense
            already knows what the signals are, even if they do not yet have meaning. In this paper we relax this assumption, and
            develop a simple model we call an attention game in which agents have to learn which feature in their environment is the signal.
            We demonstrate that simple reinforcement learning agents can still learn to coordinate in contexts in which (i) the agents do not already
            know what the signal is and (ii) the other features in the agents&apos; environment are uncorrelated with the signal. Furthermore, we show that,
            in the cases in which other features are correlated with the signal, there is a surprising trade-off between learning what the signal is, and
            success in action. We show that the mutual information between a signal and a feature plays a key role in governing the accuracy and attention of the agent.
          </Pub>

          <Pub
            id="pub-correlated-conventions"
            title="The Invention and Evolution of Correlated Conventions"
            venue={
              <>
                <a href="https://www.journals.uchicago.edu/doi/10.1086/717161">British Journal for the Philosophy of Science</a> (2025),
                with <a href="https://faculty.sites.uci.edu/skyrms/">Brian Skyrms</a>
              </>
            }
          >
            An important feature of many conventions is that the agents use an asymmetry to coordinate their behaviour.
            We call these correlated conventions. However, a puzzle arises: since any asymmetry works as well as any other,
            what are the relevant asymmetries on which a given population founds its correlated conventions? In order to gain
            traction on this question we need an account of both the invention and evolution of correlated conventions. Invention has
            remained largely unexplored in the literature. In this paper we provide a simple model of the origin and subsequent dynamics of
            correlated conventions. This model can serve as a base for future investigation in this direction.
          </Pub>

          <Pub
            id="pub-belief-standards"
            title="Standards for Belief Representations in LLMs"
            venue={
              <>
                <a href="https://link.springer.com/article/10.1007/s11023-024-09709-6">Minds and Machines</a> (2025), with {Ben}
              </>
            }
          >
            As large language models (LLMs) continue to demonstrate remarkable abilities across various domains, computer scientists are developing methods
            to understand their cognitive processes, particularly concerning how (and if) LLMs internally represent their beliefs about the world. However,
            this field currently lacks a unified theoretical foundation to underpin the study of belief in LLMs. This article begins filling this gap
            by proposing adequacy conditions for a representation in an LLM to count as belief-like. We argue that, while the project of belief measurement
            in LLMs shares striking features with belief measurement as carried out in decision theory and formal epistemology, it also differs in ways that
            should change how we measure belief. Thus, drawing from insights in philosophy and contemporary practices of machine learning, we establish four
            criteria that balance theoretical considerations with practical constraints. Our proposed criteria include accuracy, coherence, uniformity,
            and use, which together help lay the groundwork for a comprehensive understanding of belief representation in LLMs. We draw on empirical work
            showing the limitations of using various criteria in isolation to identify belief representations.
          </Pub>

          <Pub
            id="pub-pwea"
            title="Prediction with Expert Advice Applied to the Problem of Prediction with Expert Advice"
            venue={
              <>
                <a href="https://link.springer.com/article/10.1007/s11229-022-03809-5">Synthese</a> (2022)
              </>
            }
          >
            We often need to have beliefs about things on which we are not experts. Luckily, we often have access to expert judgements on such topics.
            But how should we form our beliefs on the basis of expert opinion when experts conflict in their judgments? This is the core of the novice/2-expert
            problem in social epistemology. A closely related question is important in the context of policy making: how should a policy maker use expert judgments
            when making policy in domains in which she is not herself an expert? This question is more complex, given the messy and strategic nature of politics. In
            this paper we argue that the prediction with expert advice (PWEA) framework from machine learning provides helpful tools for addressing these problems.
            We outline conditions under which we should expect PWEA to be helpful and those under which we should not expect these methods to perform well.
          </Pub>

          <Pub
            id="pub-pac"
            title="PAC Learning and Occam's Razor"
            venue={
              <>
                <a href="https://doi.org/10.1086/709786">Philosophy of Science</a> (2020)
              </>
            }
          >
            Computer scientists have provided a distinct justification of
            Occam&apos;s Razor. Using the probably approximately correct framework,
            they provide a theorem which they claim demonstrates that we
            should favour simpler hypotheses. The argument relies on a
            philosophical interpretation of the theorem. I argue that
            although, when supplemented with additional unstated assumptions,
            the proof of the theorem is mathematically sound, the standard
            interpretation of the result in the literature is misguided and
            that a better reading does not, in fact, support Occam&apos;s Razor at
            all. In support of this, I state and prove a very similar theorem
            which, if interpreted the same way, would justify the
            contradictory Anti-Occam&apos;s Razor&mdash;the principle that we should
            favour more complex hypotheses.
          </Pub>

          {/* ---------------- BOOK REVIEWS ---------------- */}

          <h2 id="reviews" style={{ marginTop: "4rem" }}>Book Reviews</h2>

          <Pub
            id="pub-review-buckner"
            title={<>Review of Cameron J. Buckner&apos;s &ldquo;From Deep Learning to Rational Machines: What the History of Philosophy Can Teach Us about the Future of Artificial Intelligence&rdquo;</>}
            venue={
              <>
                <a href="https://www.cambridge.org/core/journals/philosophy-of-science/article/review-of-cameron-j-buckners-from-deep-learning-to-rational-machines-what-the-history-of-philosophy-can-teach-us-about-the-future-of-artificial-intelligence-cameron-j-buckner-from-deep-learning-to-rational-machines-what-the-history-of-philosophy-can-teach-us-about-the-future-of-artificial-intelligence-oxford-university-press/38939A09CEDA75735A96673256422EC9">Philosophy of Science</a> (2025), with {Bruce}
              </>
            }
          />

          <Pub
            id="pub-review-myrvold"
            title={<>Review of Wayne C. Myrvold&apos;s &ldquo;Beyond Chance and Credence&rdquo;</>}
            venue={
              <>
                <a href="https://academic.oup.com/philmat/article-abstract/30/2/273/6554573">Philosophia Mathematica</a> (2022),
                with <a href="https://www.davidpeterwallisfreeborn.com/">David Peter Wallis Freeborn</a>
              </>
            }
            links={[
              ["Free link", "https://academic.oup.com/philmat/advance-article/doi/10.1093/philmat/nkac006/6554573?guestAccessKey=be69ceb8-26ba-47c8-a05b-1be9d51e9637"],
            ]}
          />

          {/* ---------------- UNDER REVIEW & IN PROGRESS ---------------- */}

          <h2 id="inprogress" style={{ marginTop: "4rem" }}>Under Review &amp; In Progress</h2>

          <Pub
            id="pub-benign"
            title="Benign Interpolation and Occam's Razor"
            venue={
              <>
                Under review, with <a href="https://tomster.userweb.mwn.de/">Tom F. Sterkenburg</a> and{" "}
                <a href="https://romeijn.web.rug.nl/">Jan-Willem Romeijn</a>
              </>
            }
            links={[["arXiv", "https://arxiv.org/abs/2608.03386"]]}
          >
            Contemporary deep learning methods generalize well even when they fit their training data perfectly, a phenomenon known as benign
            interpolation. This phenomenon cannot be accounted for by classical statistical learning theory and has prompted a range of attempted
            new explanations in the statistics and machine learning literature. A common feature of these new proposals is an appeal to a simplicity
            preference among interpolating models, often presented as a form of Occam&apos;s razor. We clarify this debate for a philosophical audience
            and argue that this new appeal to simplicity creates an explanatory gap. The classical theory offers theorems which connect the simplicity
            of model classes to good generalization, thus underwriting methodological simplicity norms. The new accounts instead appeal to properties
            of individual models, which they interpret as a kind of simplicity. Lacking a provable connection to generalization, it is the name
            &ldquo;simplicity&rdquo; that does the work a theorem used to do, making a substantive and unargued assumption look like the application
            of a familiar methodological principle.
          </Pub>

          <Pub
            id="pub-causation"
            title="A Bayesian Reduction of Causation"
            venue={<>Preprint (2026), with {Aydin}, {Ben}, and {Bruce}</>}
            links={[
              ["Preprint", "https://aydinmohseni.com/wp-content/uploads/2026/03/A_Bayesian_Reduction_of_Causation.pdf"],
            ]}
          >
            De Finetti conjectured that causation, like chance, reduces to patterns in subjective probability. Recent results on the Pearl Causal
            Hierarchy seem to foreclose this possibility, proving that causal claims are irreducible to probabilistic ones within structural causal
            models (SCMs). We show that the reduction succeeds after all. The apparent irreducibility arises from an implicit restriction on the
            agent&apos;s representational resources: once the variable set is enriched to include naturalized intervention variables, interventional
            propositions reduce to ordinary probabilistic conditioning. To complete the subjectivist picture, we prove a de Finetti-style
            representation theorem establishing that an agent with exchangeable credences can be represented as simultaneously learning causal
            structure and the probabilities within that structure via standard Bayesian updating. We then show how this framework bears on the debate
            between evidential and causal decision theory, arguing that causal decision theory functions as a useful approximation of evidential
            reasoning over the full algebra. Within SCMs, causal reasoning reduces to subjective probability.
          </Pub>

          <Pub
            id="pub-cartesian"
            title="Cartesian Frames"
            venue={
              <>
                Preprint (2021), with <a href="http://scott.garrabrant.com/">Scott Garrabrant</a> and{" "}
                <a href="https://josiahlopezwild.com">Josiah Lopez-Wild</a>
              </>
            }
            links={[["arXiv", "https://arxiv.org/abs/2109.10996"]]}
          >
            We introduce a novel framework, the theory of Cartesian frames (CF), that gives powerful tools for manipulating sets of acts.
            The CF framework takes as its most fundamental building block that an agent can freely choose from a set of available actions.
            The framework uses the mathematics of Chu spaces to develop a calculus of those sets of actions, how those actions change at
            various levels of description, and how different agents&apos; actions can combine when agents work in concert. We discuss how this
            framework might provide an illuminating perspective on issues in decision theory and formal epistemology.
          </Pub>

          <Pub
            id="pub-grounding"
            title={<>Grounding Human&ndash;Machine Decision-Making</>}
            venue={
              <>
                Manuscript, with <a href="https://sites.google.com/view/thomas-grote/startseite">Thomas Grote</a> and{" "}
                <a href="https://www.cvernade.com/">Claire Vernade</a> &middot; available on request
              </>
            }
          >
            Machine learning systems now participate in many high-stakes decisions alongside human experts, yet the conceptual terrain remains
            fragmented. Inconsistent terminology and underspecified frameworks hinder study design and obscure when findings transfer across research
            contexts. We develop a unified decision-theoretic framework capturing four types of human-machine decision-making: human-in-the-loop (ML
            systems decide while humans monitor), decision-aid (humans decide with ML predictions), complementarity (division of labor by respective
            strengths), and collective decision-making (independent judgments combined by aggregation). Grounding these types in common formal
            apparatus reveals structural similarities often missed when studied in isolation, and predicts which failure modes can arise in each type.
            The framework connects the decision-aid literature to results from formal epistemology that characterize exactly when deference to a
            machine is rational, and it identifies two intervention points: modifying what information the machine communicates versus how humans
            respond. We extend these foundations to sequential settings where both parties learn over time, and discuss applications to attributing
            responsibility for erroneous decisions in human-machine systems.
          </Pub>

          <Pub
            id="pub-subnat"
            title="Subjective Naturalism & Act Probabilities"
            venue={<>Manuscript &middot; available on request</>}
          >
            Can an agent assign probabilities to her own acts while she deliberates? This question splits decision theorists into two broad camps: those
            that believe deliberation crowds out prediction, and those who believe that deliberation welcomes prediction. I argue that philosophers are divided because
            they respond in different ways to a threat posed to agency for a certain kind of
            naturalized agent. I introduce two conditions that capture this kind of subjective naturalism: richness and austerity. I show that taking these two conditions
            poses a dilemma, and that both camps respond to the dilemma by violating
            one of these conditions. I provide a novel account of agency, called desirability
            tracking, which allows us to avoid the violations of richness that the crowding out camp commits, while also avoiding the violations of austerity that the
            welcoming camp commits. The account also allows us to extract the decision
            problems an agent takes herself to face from her own attitudes, as opposed to
            imposing them from the outside.
          </Pub>

          <Pub
            id="pub-measure"
            title="Measure for Measure"
            venue={<>Manuscript &middot; available on request</>}
          >
            Quantum mechanics is one of our most successful physical theories.
            One prominent interpretation of quantum mechanics is the Everett
            interpretation, also called the &ldquo;Many-Worlds Interpretation.&rdquo;
            However, the Everett interpretation gives rise to a puzzle:
            probability seems to be absent from the theory. This is a problem,
            because the evidence we have for quantum mechanics is that its
            probabilistic predictions agree with the statistics of measurement
            outcomes. David Wallace has argued that we can use decision theory,
            via a representation theorem, to solve this probability puzzle. I
            argue that providing a representation theorem in which an agent is
            representable as maximizing a utility function with respect to what
            is mathematically a probability function is not sufficient to derive
            probability in the sense of credence, and thus that this solution to
            the problem fails.
          </Pub>

          {/* ---------------- POEM ---------------- */}

          <div className="twelve columns" style={{ marginLeft: 0 }}>
            <hr
              style={{
                marginTop: "3.5rem",
                marginBottom: "3.5rem",
                border: "none",
                borderTop: "1px solid rgba(0,0,0,0.08)",
                width: "6rem",
              }}
            />
          </div>

          <div
            className="twelve columns"
            style={{ marginLeft: 0, marginBottom: "5rem" }}
          >
            <h6>Sapere Aude</h6>
            <p style={{ marginBottom: "1.5rem" }}>
              <a href="https://bpb-us-e2.wpmucdn.com/faculty.sites.uci.edu/dist/c/190/files/2011/03/ConvergenceTruthFinal.pdf">Knowledge enormous makes a God of me.</a><br />
              <a href="https://sites.socsci.uci.edu/~bskyrms/bio/books/signals.pdf">Names</a>, <a href="http://fitelson.org/piksi/piksi_22/the_logic_of_decision.pdf">deeds</a>, <a href="https://users.ece.cmu.edu/~gamvrosi/thelastq.html">grey legends</a>, <a href="https://philpapers.org/rec/KIRTAE-3">dire events</a>, rebellions, <br />
              Majesties, <a href="https://www.gutenberg.org/files/6424/6424-h/6424-h.htm">sovran voices</a>, <a href="https://www.gutenberg.org/files/1138/1138-h/1138-h.htm">agonies</a>, <br />
              <a href="https://www.pnas.org/doi/pdf/10.1073/pnas.24.12.556">Creations</a> and <a href="https://projecteuclid.org/journals/rocky-mountain-journal-of-mathematics/volume-32/issue-4/The-Discovery-of-Forcing/10.1216/rmjm/1181070010.full"> destroyings</a>, <a href="https://projecteuclid.org/journals/annals-of-mathematical-statistics/volume-33/issue-3/Merging-of-Opinions-with-Increasing-Information/10.1214/aoms/1177704456.full">all at once <br />
                Pour into</a> the <a href="https://link.springer.com/article/10.1007/s10992-018-9496-8">wide hollows</a> of my brain, <br />
              And <a href="http://www.vavatch.co.uk/books/banks/cultnote.htm">deify me</a>, as if some blithe wine <br />
              Or <a href="https://www.gutenberg.org/files/718/718-h/718-h.htm">bright elixir peerless</a> I had drunk, <a href="https://repositorio.ufsc.br/bitstream/handle/123456789/157358/The%20Mortal%20Immortal%20-%20Mary%20Shelley.pdf?sequence=1&isAllowed=y"> <br />
                And so become immortal</a>. . .
            </p>
            <p>— John Keats, Hyperion</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Research;
